import { SyncManager } from '@/firebase/SyncManager'
import { selectUser } from '@/store/slices/user/selectors'
import { DotLottiePlayer } from '@dotlottie/react-player'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
// @ts-ignore
import cloudAnimation from '@/assets/lottie/cloud_animation.lottie'
import { lightTheme } from '@/customTheme'
import useAppContext from '@/hooks/useAppContext'
import useModal from '@/hooks/useModal'
import { CLOSE_TOAST_COMPLETED, ShowModalType } from '@/scenes/engine/common/constants'
import Icons from '@/scenes/Editor/components/Icons'
import { useAppDispatch } from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { Z_INDEX } from '@/constants/z-index'
import { SyncStatus } from '@/shared/sync/dataTypes/SyncStatus'
import { LayoutUtils } from '@/utils/LayoutUtils'

interface toastType {
  message: string | JSX.Element
  icon: JSX.Element | null
  background: string
  color: string
  hasClose?: boolean,
  overrideStyle?: any
}
const baseStyle = {
  top: '80px',
  height: '40px',
  minWidth: '100px',
  maxWidth: '548px',
  transform: `translateX(-50%)`,
  borderRadius: '20px',
  padding: '8px 16px',
  alignItems: 'center',
  gap: '16px',
  display: 'flex',
  zIndex: Z_INDEX.MESSAGE_TOAST,
}

function MessageToast() {
  const { id = null } = useParams<{ id: string }>()
  const { openToast, setOpenToast } = useAppContext()
  const { setShowModalType } = useModal()
  const user = useSelector(selectUser)
  const { t } = useTranslation()
  const location = useLocation()
  const [leftOffset, setLeftOffset] = useState('44px')
  const [rightOffset, setRightOffset] = useState('356px')
  const toastRef = useRef()
  const dispatch = useAppDispatch()
  
  const onSignIn = () => {
    setShowModalType(ShowModalType.SIGN_IN)
    dispatch(
      setOpenModalTryPremium({
        isOpen: false,
        source: null,
        callback: null,
        opened: false,
      })
    )
  }

  const toastTypes: { [key: string]: toastType } = {
    reconnecting: {
      message: t('Trying to reconnect. Changes will be saved once connection is restored.'),
      icon: <DotLottiePlayer src={cloudAnimation} autoplay loop style={{ width: '20px', height: '24px' }} />,
      background: '#FCD34D',
      color: lightTheme.colors.blackGray,
      overrideStyle: {}
    },
    connectionRestored: {
      message: t('Connection restored.'),
      icon: <Icons.CloudCompleted />,
      background: '#0DB08C',
      color: lightTheme.colors.white,
      overrideStyle: {}
    },
    noInternet: {
      message: t('No internet connection, issues may occur.'),
      icon: <Icons.Caution viewBox="0 0 18 19" />,
      background: '#FCD34D',
      color: lightTheme.colors.blackGray,
      overrideStyle: {}
    },
    nonSignUpUser: {
      message: (
        <>
          {`${t('Your design is not saved')}. `}
          <span
            onClick={onSignIn}
            style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: '500' }}
          >
            {t('Sign in')}
          </span>
          {` ${t('to save')}.`}
        </>
      ),
      icon: null,
      background: lightTheme.colors.white,
      color: lightTheme.colors.blackGray,
      hasClose: true,
      overrideStyle: {
        display: 'none',
        top: '20px',
      }
    },
  }

  useEffect(() => {
    (async () => {
      if (user && id) {
        let syncItem = await SyncManager.shared.getSyncItem(id)
        if (syncItem && syncItem.mostUpdatedVersion.syncStatus !== SyncStatus.Updated) {
          setOpenToast('SyncInProgress')
        }
      }
    })()
  }, [user])

  useEffect(() => {
    if (!location) {
      return
    }
    setLeftOffset(location.pathname.includes('/create') ? '130px' : `${LayoutUtils.ADD_MENU_AND_MARGIN_LEFT}px`)
    setRightOffset(location.pathname.includes('/create') ? '0px' : `${LayoutUtils.PAGES_WIDTH}px`)
  }, [location])

  useEffect(() => {
    const toastEl = toastRef.current as HTMLDivElement
    if (!openToast) {
      return
    }
    switch (openToast) {
      case 'connectionRestored':
        setTimeout(() => {
          setOpenToast('')
        }, CLOSE_TOAST_COMPLETED)
        break
      case 'nonSignUpUser':
        toastEl.style.top = '20px'
        toastEl.style.display = 'flex'
        setTimeout(() => {
          toastEl.style.transition = 'transform 0.3s ease-in-out'
          toastEl.style.transform = `translateX(-50%) translateY(60px)`
        }, 0);
        break
      default:
        toastEl.style.transition = ''
        toastEl.style.transform = `translateX(-50%) translateY(0px)`
    }
  }, [openToast])

  const onCloseToast = () => {
    const toastEl = toastRef.current as HTMLDivElement
    toastEl.style.transform = `translateX(-50%) translateY(0px)`
    setTimeout(() => {
      setOpenToast('')
      toastEl.style.transition = ''
    }, 300)
  }

  return (
    <div
      ref={toastRef}
      style={{
        position: 'fixed',
        left: `calc((100vw - ${rightOffset}) / 2 + ${leftOffset})`,
        background: toastTypes[openToast]?.background,
        transform: `translateX(-50%)`,

        color: toastTypes[openToast]?.color,
        ...baseStyle,
        ...toastTypes[openToast]?.overrideStyle,
        pointerEvents: openToast ? 'auto' : 'none'
      }}
    >
      {openToast && toastTypes[openToast].icon}
      <p id="toast-content" style={{ ...lightTheme.typography.Small14regular, letterSpacing: '-0.084px' }}>
        {openToast && toastTypes[openToast].message}
      </p>
      {toastTypes[openToast]?.hasClose ? (
        <div
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.1)',
            cursor: 'pointer',
          }}
          onClick={onCloseToast}
        >
          <Icons.Close size={24} fill={lightTheme.colors.blackGray} />
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

export default MessageToast
